import Button from "@mui/material/Button";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useContext, useEffect, useRef } from "react";
import { DashboardContext } from "../../DashboardContext/DashboardContextProvider";
import { FormContext } from "./context/FormProvider";
import { handleNext } from "./Helper/formbtnhelper";
import SavedIndicator from "./SavedIndicator/SavedIndicator";
import useSubmitForm from "./Slides/Hooks/setSubmitForm";
import useCreateRequest from "./Slides/Hooks/useCreateRequest";
import "./styles/formbtns.css";

const FormButtons = () => {
  const {
    slide1,
    pageNumber,
    setPageNumber,
    isLoading,
    setIsLoading,
    timeStamp,
    requestStatus,
    setActiveUserId,
    setTimeStamp,
    setOpenCheckModal,
    isReady,
    setIsReady,
    slide12,
    slideStatus,
  } = useContext(FormContext);
  const { navigate, user, clientId, projectId, client } =
    useContext(DashboardContext);
  const submitButtonRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  // | CREATE REQUEST MUTATION |
  const createForm = useCreateRequest(
    clientId,
    setPageNumber,
    user,
    setActiveUserId,
    setTimeStamp,
    setIsLoading,
    enqueueSnackbar,
  );

  const submitForm = useSubmitForm(
    clientId,
    projectId,
    isReady,
    setIsReady,
    enqueueSnackbar,
    setIsLoading,
    slide12,
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    if (isReady === true) {
      submitButtonRef.current.click();
      setIsReady(false);
    }
  }, [isReady]);

  // *BACK HANDLER
  const handleBack = async (e) => {
    const allowedUsers = ["Courtney Breen", "Argus Admin"];
    if (
      requestStatus === "A" &&
      pageNumber === 7 &&
      !allowedUsers.includes(user.name)
    ) {
      return;
    } else if (
      requestStatus === "A" &&
      pageNumber === 8 &&
      !allowedUsers.includes(user.name)
    ) {
      setPageNumber(7);
      navigate(`./form/${clientId}/${projectId}/7`, { replace: true });
      return;
    } else if (
      requestStatus === "A" &&
      pageNumber === 11 &&
      !allowedUsers.includes(user.name)
    ) {
      setPageNumber(8);
      navigate(`./form/${clientId}/${projectId}/8`, { replace: true });
      return;
    }
    if (client.shortName !== "Greystone") {
      setIsReady(false);
      setPageNumber(() => pageNumber - 1);
      navigate(`./form/${clientId}/${projectId}/${pageNumber - 1}`, {
        replace: true,
      });
    } else {
      setIsReady(false);
      if (pageNumber === 3 || pageNumber === 5 || pageNumber === 11) {
        setPageNumber(() => pageNumber - 2);
        navigate(`./form/${clientId}/${projectId}/${pageNumber - 2}`, {
          replace: true,
        });
      } else {
        setPageNumber(() => pageNumber - 1);
        navigate(`./form/${clientId}/${projectId}/${pageNumber - 1}`, {
          replace: true,
        });
      }
    }
  };

  const redirectToFirstIncompleteSlide = (slideStatus) => {
    const incompleteSlides = Object.entries(slideStatus)
      .filter(([_, status]) => status !== "complete" && status !== "disabled")
      .map(([slideKey, _]) => parseInt(slideKey.replace("slide", ""), 10))
      .sort((a, b) => a - b);

    return incompleteSlides;
  };

  // *SUBMIT HANDLER
  const handleSubmit = async (e) => {
    e.preventDefault();

    const incompleteSlides = redirectToFirstIncompleteSlide(slideStatus);

    if (incompleteSlides.length > 0) {
      const firstIncompleteSlide = incompleteSlides[0];
      setPageNumber(firstIncompleteSlide);
      navigate(`./form/${clientId}/${projectId}/${firstIncompleteSlide}`, {
        replace: true,
      });
      enqueueSnackbar(
        `Please complete all required fields before submitting.`,
        { variant: "warning" },
      );
      return;
    }

    if (!projectId) {
      enqueueSnackbar("Project ID is missing", { variant: "error" });
      return;
    } else if (!isReady) {
      setOpenCheckModal(true);
    } else {
      try {
        await submitForm.mutateAsync();
      } catch (error) {
        console.log("SUBMIT ERROR: ", error);
        enqueueSnackbar("Error submitting form. Please try again.", {
          variant: "error",
        });
      }
    }
  };

  return (
    <>
      <div className="form-btns">
        <Button
          id={
            isReady ||
            submitForm.isPending ||
            pageNumber === 0 ||
            (pageNumber === 1 && projectId !== undefined)
              ? "disabled"
              : "back-btn"
          }
          variant="contained"
          onClick={
            pageNumber === 0 || (pageNumber === 1 && projectId)
              ? () => {}
              : handleBack
          }
          disabled={
            isReady ||
            submitForm.isPending === true ||
            pageNumber === 0 ||
            (pageNumber === 1 && projectId)
              ? true
              : false
          }
        >
          Back
        </Button>
        {pageNumber === 0 || !projectId ? null : (
          <SavedIndicator
            timeStamp={timeStamp ? timeStamp : null}
            isLoading={isLoading}
          />
        )}
        {pageNumber <= 10 ? (
          <Button
            id={
              (pageNumber === 0 && !clientId) ||
              (pageNumber >= 1 && slide1.projectName === "") ||
              (pageNumber === 1 && createForm.isPending === true)
                ? "disabled"
                : "next-btn"
            }
            variant="contained"
            onClick={(e) =>
              handleNext(
                e,
                clientId,
                projectId,
                slide1,
                pageNumber,
                setPageNumber,
                pageNumber + 1,
                createForm,
                queryClient,
                requestStatus,
              )
            }
            disabled={
              // If the client ID is not found, disable the button
              (pageNumber === 0 && !clientId) ||
              (pageNumber >= 1 && slide1.projectName === "") ||
              // If the form is being created, disable the button
              (pageNumber === 1 && createForm.isPending === true)
            }
          >
            Next
          </Button>
        ) : (
          <Button
            id={
              requestStatus === "IP" ||
              requestStatus === "I" ||
              requestStatus === "P" ||
              requestStatus === "RV" ||
              requestStatus === "A"
                ? "submit-btn"
                : "disabled"
            }
            type="submit"
            variant="contained"
            onClick={handleSubmit}
            ref={submitButtonRef}
            disabled={submitForm.isPending === true || isLoading}
          >
            Submit
          </Button>
        )}
      </div>
    </>
  );
};

export default FormButtons;
